
import { Options, Vue } from "vue-class-component";
import firebase from "firebase/app";
import MButton from "@/components/MButton.vue";
import { tutorPasswordState } from "@/api/tutorPassword";
import { tutorUpdatePassword } from "@/api/tutor";
import { signOut } from "@/api/auth";
import {
  isValidTutorPassword,
  TutorPasswordSupport
} from "@/entities/tutor_password_policy";
import {
  formatPasswordDeadline,
  TUTOR_PASSWORD_RULES
} from "@/utils/tutorPasswordFormat";
import ChannelService from "@/plugins/ChannelService";

type RuleState = "pending" | "met" | "broken";

/**
 * 先生のパスワード変更画面。期限切れ・初回は App.vue がアプリの代わりにこの画面だけを出し、
 * 期限前はユーザー設定・ホームのお願いから開く
 */
@Options({ components: { MButton } })
export default class ChangePassword extends Vue {
  password = "";
  confirmation = "";
  showPassword = false;
  showConfirmation = false;
  confirmBlurred = false;
  submitted = false;
  busy = false;
  done = false;
  error = "";
  support: TutorPasswordSupport | null = null;
  supportError = false;
  showAllTopics = false;
  visibleTopicCount = 3;
  get status() {
    return tutorPasswordState.status;
  }
  get required() {
    return !!this.status?.required;
  }
  get email() {
    return firebase.auth().currentUser?.email || "";
  }
  get deadline() {
    return this.status
      ? formatPasswordDeadline(this.status.requiredAt, Date.now())
      : "";
  }
  get lead() {
    if (this.status?.initialRequired)
      return "いまのパスワードは、管理者が用意した仮のものです。あなただけが知っているパスワードに変更すると、使いはじめられます。";
    if (this.required)
      return `変更期限の${this.deadline}を過ぎました。新しいパスワードに変更すると、すぐに元どおりお使いいただけます。`;
    return "";
  }
  get returnTo() {
    const from = this.$route.query.from;
    return typeof from === "string" &&
      from.startsWith("/") &&
      !from.startsWith("//")
      ? from
      : "/home";
  }
  get validPassword() {
    return isValidTutorPassword(this.password);
  }
  get matches() {
    return this.confirmation.length > 0 && this.confirmation === this.password;
  }
  get confirmMessage() {
    if (!this.confirmation)
      return this.submitted
        ? "確認のため、同じパスワードをもう一度入力してください"
        : "";
    if (this.matches) return "一致しています";
    return this.confirmBlurred ||
      this.submitted ||
      this.confirmation.length >= this.password.length
      ? "上のパスワードと一致しません"
      : "";
  }
  get confirmError() {
    return !!this.confirmMessage && !this.matches;
  }
  get rules() {
    return TUTOR_PASSWORD_RULES.map(rule => {
      let state: RuleState = "pending";
      if (rule.isBroken(this.password)) state = "broken";
      else if (this.password && rule.isMet(this.password)) state = "met";
      else if (this.submitted && !rule.prohibits) state = "broken";
      return { id: rule.id, label: rule.label, state };
    });
  }
  get topics() {
    return this.support?.topics || [];
  }
  get visibleTopics() {
    return this.showAllTopics
      ? this.topics
      : this.topics.slice(0, this.visibleTopicCount);
  }
  ruleTextClass(state: RuleState) {
    return {
      pending: "text-gray-500",
      met: "text-green-700",
      broken: "text-red-600"
    }[state];
  }
  ruleIconClass(state: RuleState) {
    return {
      pending: "border border-gray-300",
      met: "bg-green-100 text-green-700",
      broken: "bg-red-100 text-red-600"
    }[state];
  }
  topicDate(seconds: number) {
    return new Intl.DateTimeFormat("ja-JP", {
      timeZone: "Asia/Tokyo",
      year: "numeric",
      month: "long",
      day: "numeric"
    }).format(seconds * 1000);
  }
  mounted() {
    if (this.required) void this.loadSupport();
  }
  async loadSupport() {
    this.supportError = false;
    try {
      const result = await firebase
        .app()
        .functions("asia-northeast1")
        .httpsCallable("tutor_password_status")({
        action: "support",
        service: "admin"
      });
      this.support = result.data;
      if (this.support?.useChannelTalk)
        ChannelService.boot({
          pluginKey: "e82d60ee-f3f8-4b12-9e83-4e55eff0132d",
          memberId: firebase.auth().currentUser?.uid,
          hideChannelButtonOnBoot: true,
          language: "ja",
          profile: {
            name: this.support.name,
            schoolName: this.support.schoolName
          }
        });
    } catch {
      this.supportError = true;
    }
  }
  contact() {
    (window as any).ChannelIO?.("showMessenger");
  }
  cancel() {
    this.$router.push(this.returnTo);
  }
  async logout() {
    await signOut();
    window.location.assign("/login");
  }
  async submit() {
    this.submitted = true;
    if (this.busy) return;
    if (!this.validPassword) {
      document.getElementById("tutor-new-password")?.focus();
      return;
    }
    if (!this.matches) {
      document.getElementById("tutor-confirm-password")?.focus();
      return;
    }
    const uid = firebase.auth().currentUser?.uid;
    if (!uid) return;
    this.busy = true;
    this.error = "";
    try {
      await tutorUpdatePassword(
        firebase.firestore().doc("roles/" + uid),
        this.password
      );
      this.password = "";
      this.confirmation = "";
      this.done = true;
      // 認証を更新したので、画面の状態ごと作り直す
      window.setTimeout(
        () => window.location.assign(this.required ? "/home" : this.returnTo),
        1200
      );
    } catch (e) {
      const code = (e as { code?: string } | null)?.code ?? "";
      this.error =
        code === "unavailable" ||
        code === "deadline-exceeded" ||
        !navigator.onLine
          ? "通信がうまくいきませんでした。通信状況を確認して、もう一度お試しください。"
          : "パスワードを変更できませんでした。時間をおいてもう一度お試しください。続く場合はお問い合わせください。";
      this.busy = false;
    }
  }
}
